import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Button, { ButtonGroup } from 'components/buttons/Button';

import { Loader } from '../../../../components/Loader/Loader';
import { isAfterDeliverUntil, isBeforeDeliverFrom, isToday } from 'utils/date';

import { useFetchOrders } from 'hooks/useFetchOrders';

import '../Widget.scss';
import './DeviationWidget.scss';
import { OrderClazz, OrderSearchStatus } from '../../../../types/orderTypes';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { ReqEnum } from '../../../../const/requestStatus';
import analyticsWrapper from '../../../../utils/analyticsWrapper';

function setBackgroundColorByStatus(diff: number): string {
  if (!diff) {
    return 'white';
  }
  if (Math.abs(diff) > 5 && Math.abs(diff) < 15) {
    return '#ffe200';
  } else if (Math.abs(diff) < 5) {
    return '#0eb227';
  } else {
    return '#dd4912';
  }
}

function setColorByStatus(diff: number): string {
  if (!diff) {
    return 'white';
  }
  if (Math.abs(diff) > 5 && Math.abs(diff) < 15) {
    return 'black';
  } else {
    return 'white';
  }
}

export default function DelayedDeviationWidget() {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [loadData, setLoadData] = useState<boolean>(false);
  const [filterDay, setFilterDay] = useState<number>(0);

  const timeToShowOrdersFor = dayjs()
    .add(filterDay, 'day')
    .format('YYYY-MM-DD');

  const changeDay = (changeInt: number) => {
    setFilterDay(filterDay + changeInt);
  };

  return (
    <div className="widget">
      <div className="widget__header">
        <h5>Time delivery deviations</h5>
        {collapsed && (
          <span className="widget__expand">
            <span>Click to expand</span>
            <Button
              title={<FaAngleDown />}
              onClick={() => {
                analyticsWrapper.event({
                  category: 'user-action',
                  action: 'time-deviation-widget-expand',
                });
                setCollapsed(false);
                setLoadData(true);
              }}
            />
          </span>
        )}
        {!collapsed && (
          <span className="widget__expand">
            <span>Click to collapse</span>
            <Button title={<FaAngleUp />} onClick={() => setCollapsed(true)} />
          </span>
        )}
      </div>
      <div style={{ height: 10 }} />
      <ButtonGroup>
        <Button title="<" onClick={() => changeDay(-1)} disabled={collapsed} />
        <Button
          style={{ width: '100%' }}
          title={dayjs(timeToShowOrdersFor, 'YYYY-MM-DD').format(
            'dddd DD MMMM'
          )}
          disabled={collapsed}
        />
        <Button
          title=">"
          onClick={() => changeDay(+1)}
          disabled={collapsed || isToday(timeToShowOrdersFor)}
        />
      </ButtonGroup>
      {loadData && (
        <DelayedDeviationsDetailsWidget
          filterDay={filterDay}
          collapsed={collapsed}
        />
      )}
    </div>
  );
}

function DelayedDeviationsDetailsWidget(props: {
  filterDay: number;
  collapsed: boolean;
}) {
  const [startDate] = useState(dayjs().add(-10, 'day'));
  const [endDate] = useState(dayjs().add(+1, 'day'));

  const [orders, fetchStatus] = useFetchOrders(
    OrderSearchStatus.Done,
    startDate,
    endDate
  );

  if (props.collapsed) {
    return <></>;
  }
  if (fetchStatus === ReqEnum.PENDING || fetchStatus === ReqEnum.INIT) {
    return (
      <div className="widget__loader">
        <Loader type="spinner" size="large" />
      </div>
    );
  }

  const timeToShowOrdersFor = dayjs()
    .add(props.filterDay, 'day')
    .format('YYYY-MM-DD');

  const ordersForGivenDay = orders.filter(
    order =>
      dayjs(order.deliverFrom).format('YYYY-MM-DD') === timeToShowOrdersFor
  );

  const ordersForGivenDayWithDelayedDeviation = (
    ordersForGivenDay: OrderClazz[]
  ) =>
    ordersForGivenDay.filter(order =>
      isAfterDeliverUntil(order.deliverUntil, order.deliveredAt)
    );

  const ordersForGivenDayWithToEarlyDeviation = (
    ordersForGivenDay: OrderClazz[]
  ) =>
    ordersForGivenDay.filter(order =>
      isBeforeDeliverFrom(order.deliverFrom, order.deliveredAt)
    );

  const showDelayedDeviationsTable = (order: OrderClazz) => {
    const diff = dayjs(order.deliveredAt).diff(order.deliverUntil, 'minute');
    return (
      <tr key={`${order.id}-${Math.random()}`}>
        <td>
          <Link to={`/orders/${order.id}`} className="link__clean">
            {order.id}
          </Link>
        </td>
        <td>{order.courierName} </td>
        <td>{dayjs(order.deliverUntil).format('HH.mm')}</td>
        <td>{dayjs(order.deliveredAt).format('HH.mm')}</td>
        <td
          style={{
            backgroundColor: setBackgroundColorByStatus(diff),
            color: setColorByStatus(diff),
          }}
        >
          +{diff}
        </td>
      </tr>
    );
  };

  const showToEarlyDeviationsTable = (order: OrderClazz) => {
    const diff = dayjs(order.deliveredAt).diff(order.deliverFrom, 'minute');
    return (
      <tr key={`${order.id}-${Math.random()}`}>
        <td>
          <Link to={`/orders/${order.id}`} className="link__clean">
            {order.id}
          </Link>
        </td>
        <td>{order.courierName} </td>
        <td>{dayjs(order.deliverFrom).format('HH.mm')}</td>
        <td>{dayjs(order.deliveredAt).format('HH.mm')}</td>
        <td
          style={{
            backgroundColor: setBackgroundColorByStatus(diff),
            color: setColorByStatus(diff),
          }}
        >
          {diff}
        </td>
      </tr>
    );
  };

  const earlyDeviations =
    ordersForGivenDayWithToEarlyDeviation(ordersForGivenDay);

  const delayedDeviations =
    ordersForGivenDayWithDelayedDeviation(ordersForGivenDay);

  return (
    <>
      {earlyDeviations.length > 0 && (
        <div style={{ width: '100%' }}>
          <div>To early delivery Deviations</div>
          <table className="deviation-window-table">
            <thead>
              <tr>
                <th>OrderId</th>
                <th>Courier</th>
                <th>Deliver from</th>
                <th>Before</th>
                <th>Diff</th>
              </tr>
            </thead>

            <tbody>
              {earlyDeviations.map(order => {
                return showToEarlyDeviationsTable(order);
              })}
            </tbody>
          </table>
        </div>
      )}
      {delayedDeviations.length > 0 && (
        <div style={{ width: '100%' }}>
          <div>Delayed delivery Deviations</div>
          <table className="deviation-window-table">
            <thead>
              <tr>
                <th>OrderId</th>
                <th>Courier</th>
                <th>Deliver until</th>
                <th>After</th>
                <th>Diff</th>
              </tr>
            </thead>

            <tbody>
              {ordersForGivenDayWithDelayedDeviation(ordersForGivenDay).map(
                order => {
                  return showDelayedDeviationsTable(order);
                }
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
